import React, {
  ComponentProps,
  FocusEventHandler,
  KeyboardEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import cn from "classnames";
import { createEditor, Descendant, Transforms } from "slate";
import { withHistory } from "slate-history";
import { Editable, Slate, withReact } from "slate-react";

import EmailDynamicVariable from "@mapmycustomers/shared/types/email/EmailDynamicVariable";
import { EntityTypesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { useConfigProvider } from "../../ConfigProvider";
import { MmcEditor } from "../../type/slate-types";
import ElementType from "../../type/slate/ElementType";
import resetEditor from "../../utils/resetEditor";
import EmailDynamicVariableView from "../EmailDynamicVariableView";
import EmailContext from "../EmailEditorsContext";
import withDynamicVariables from "../util/withDynamicVariables";

import styles from "./EmailSubject.module.scss";
import deserializeFromString from "./util/deserializeFromString";
import serializeToString from "./util/serializeToString";
import withSingleLine from "./util/withSingleLine";

const SUBJECT_MAX_LENGTH = 998;

type SlateProps = ComponentProps<typeof Slate>;
type EditableProps = ComponentProps<typeof Editable>;

interface EmailSubjectProps {
  className?: string;
  disabled?: boolean;
  dynamicVariables?: EmailDynamicVariable[];
  entityType?: EntityTypesSupportedByEmailFeature;
  maxLength?: number;
  onBlur?: FocusEventHandler;
  onChange?: (value: string) => void;
  onFocus?: FocusEventHandler;
  onKeyDown?: KeyboardEventHandler;
  placeholder?: string;
  readOnly?: boolean;
  value?: string;
}

const EmailSubject = ({
  className,
  disabled,
  dynamicVariables,
  entityType,
  maxLength = SUBJECT_MAX_LENGTH,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  readOnly,
  value = "",
}: EmailSubjectProps) => {
  const emailContext = useContext(EmailContext);
  const configProvider = useConfigProvider();

  const [initialElements] = useState(() => {
    return deserializeFromString(value);
  });

  const elementsRef = useRef<Descendant[]>(initialElements);

  const valueRef = useRef(value);

  const editorRef = useRef<MmcEditor>(
    (() => {
      return [withDynamicVariables, withSingleLine].reduce(
        (editor, plugin) => plugin(editor),
        withReact(withHistory(createEditor()))
      );
    })()
  );

  const editor = editorRef.current!;

  const handleChange = useDynamicCallback<NonNullable<SlateProps["onChange"]>>(
    (nodes: Descendant[]) => {
      const value = serializeToString(nodes);

      if (elementsRef.current !== nodes) {
        onChange?.(value);
      }

      elementsRef.current = nodes;
      valueRef.current = value;
    }
  );

  const handleKeyDown = useDynamicCallback<KeyboardEventHandler>((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
    onKeyDown?.(e);
  });

  const renderElement = useDynamicCallback<NonNullable<EditableProps["renderElement"]>>(
    ({ attributes, children, element }) => {
      if (element.type === ElementType.DYNAMIC_VARIABLE) {
        const dynamicVariable =
          entityType &&
          dynamicVariables?.find(
            (dynamicVariable) =>
              dynamicVariable.prefix === element.prefix && dynamicVariable.name === element.name
          );

        return (
          <span {...attributes} contentEditable={false}>
            {dynamicVariable && (
              <EmailDynamicVariableView dynamicVariable={dynamicVariable} entityType={entityType} />
            )}
          </span>
        );
      }

      return <span {...attributes}>{children}</span>;
    }
  );

  useEffect(() => {
    if (valueRef.current !== value) {
      resetEditor(editor);

      const elements = deserializeFromString(value);

      Transforms.insertNodes(editor, elements);

      elementsRef.current = editor.children;
      valueRef.current = value;
    }
  }, [editor, value]);

  useEffect(() => {
    emailContext.subjectEditor = editor;
  }, [emailContext, editor]);

  return (
    <Slate
      editor={editor}
      initialValue={initialElements}
      onChange={disabled ? undefined : handleChange}
    >
      <Editable
        className={cn(styles.editable, className, { "mmc-js-empty": !value?.trim() })}
        disabled={disabled}
        maxLength={maxLength}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? configProvider.formatMessage("ui.emailSubject.placeholder")}
        readOnly={readOnly}
        renderElement={renderElement}
      />
    </Slate>
  );
};

export default EmailSubject;
